import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { onChangeTitleDebounceTime } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import { usePublishModalAnalyticsEvent } from './usePublishModalAnalyticsEvent.ts';
import { ValidatePagePublishQuery } from './ValidatePagePublishQuery.graphql.ts';
import { displayPublishErrorFlag } from './publishHelpers.tsx';
import type {
	ValidatePagePublishQuery$data,
	ValidatePagePublishQuery$variables,
} from './__generated__/ValidatePagePublishQuery.graphql.ts';

type UsePrePublishTitleValidationArgs = {
	contentId: string | undefined;
	contentType: string;
	isUnpublishedDraft: boolean;
	flags: FlagsStateContainer;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	onBeforeValidation: () => void;
	onValidationSuccess: () => void;
	analyticsSource: string;
	optimisticTitle?: string | null;
};

export const usePrePublishTitleValidation = ({
	contentId,
	contentType,
	isUnpublishedDraft,
	flags,
	retrieveEditorTitleAndContent,
	onBeforeValidation,
	onValidationSuccess,
	analyticsSource,
	optimisticTitle,
}: UsePrePublishTitleValidationArgs): ((actionSubjectId: string) => () => Promise<void>) => {
	const intl = useIntl();

	// Changes to EditorTitle are debounced if the page is a draft, which we need to account for
	const titleDebounceTime = isUnpublishedDraft ? onChangeTitleDebounceTime : 0;

	const { createAnalyticsEvent } = usePublishModalAnalyticsEvent();

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const isLivePage = useIsCurrentPageLive();

	// Since the validatePageAndOpenModal function may be running after this component has unmounted,
	// we have to query the apolloClient directly rather than using useQuery's refetch function.
	const validatePageAndOpenModal = useCallback(
		(actionSubjectId: string) => {
			const validatePage = async () => {
				// Live Pages only save changes to the `realTitle` when it is valid,
				// so we need to use the `optimisticTitle` here to get what the user
				// has actually typed into the title field, and then show the correct flag.
				const { title: realTitle } = await retrieveEditorTitleAndContent();
				// ?? must be used here to explicitly check null and undefined because we want to use optmisticTitle if it is an empty string, which is also falsey.
				const title = optimisticTitle ?? realTitle;
				try {
					const { data } = await queryInterop<
						ValidatePagePublishQuery$data,
						ValidatePagePublishQuery$variables
					>({
						query: ValidatePagePublishQuery,
						errorPolicy: 'all',
						fetchPolicy: 'network-only',
						variables: {
							contentId: contentId ?? '',
							type: contentType,
							title,
						},
					});
					// This is the same logic used to calculate `isTitleEmpty` in EditorPrimaryToolbarComponents.
					const isTitleEmpty = !title;
					if (!data.validatePagePublish?.isValid) {
						await displayPublishErrorFlag({
							validationMessage: data.validatePagePublish?.message ?? '',
							isTitleEmpty,
							flags,
							formatMessage: intl.formatMessage,
							publish: validatePageAndOpenModal('PublishButtonInFlag'),
							isAIEnabledForContent,
							isLivePage,
						});
					} else {
						onValidationSuccess();
					}
				} catch (error) {
					if (error) {
						markErrorAsHandled(error);
						if (!containsNetworkError(error) && !isUnauthorizedError(error)) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_MODES,
							});
						}
					}
				}
			};
			return async () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'button',
						action: 'clicked',
						actionSubjectId,
						source: analyticsSource,
						attributes: {
							isUnpublishedDraft,
						},
					},
				}).fire();
				setTimeout(async () => {
					onBeforeValidation();
					await validatePage();
				}, titleDebounceTime);
			};
		},
		[
			contentId,
			contentType,
			retrieveEditorTitleAndContent,
			createAnalyticsEvent,
			flags,
			intl.formatMessage,
			titleDebounceTime,
			isUnpublishedDraft,
			analyticsSource,
			onBeforeValidation,
			onValidationSuccess,
			optimisticTitle,
			isAIEnabledForContent,
			isLivePage,
		],
	);

	return validatePageAndOpenModal;
};
